// Small date/clock helpers shared by the per-league hub tabs (Compete / Fixtures).
import { useEffect, useState } from 'react';

/** Long day heading, e.g. "Saturday 14 June". */
export function dayHeading(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Compact match-day label, e.g. "Sat 14 Jun". */
export function shortDay(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Local midnight (start of the calendar day) for an epoch-ms time. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Day label relative to `now`: "Today" / "Tomorrow", else the short date
 *  ("Sat 20 Jun"). Powers the peek-ahead day strip and the "Opens …" copy. */
export function relativeDay(ms: number, now: number = Date.now()): string {
  const diffDays = Math.round((startOfDay(ms) - startOfDay(now)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return shortDay(ms);
}

/** 24h kickoff time, e.g. "21:00". */
export function timeLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Re-renders on an interval so live countdowns tick. Minute cadence is enough for
 *  the picks deadline; pass `seconds` for a per-second clock. */
export function useNow(seconds = false): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), seconds ? 1000 : 30000);
    return () => window.clearInterval(id);
  }, [seconds]);
  return now;
}
